import React from 'react';
import {CssImageCreate} from '../../../../../type/cssTemplate';
import {getAspectRatioType} from '../../../../../util/templateImageUtils';

interface CategoryGroup {
    name: string;
    images: any[];
}

interface TemplateSidebarProps {
    show: boolean;
    onClose: () => void;
    role?: string;
    loading: boolean;
    selectedThemeName: string | null;
    selectedNewTheme: string | null;
    isDefaultThemeByName: (themeName?: string | null) => boolean;
    showNewCategoryInput: boolean;
    onShowNewCategoryInput: () => void;
    onCancelNewCategoryInput: () => void;
    newCategoryName: string;
    onChangeNewCategoryName: (value: string) => void;
    onSubmitNewCategory: () => void;
    categories: CategoryGroup[];
    createList: CssImageCreate[];
    uploadedImagesInfo: Map<string, any>;
    uploadedImages: Map<string, string>;
    onRestoreDefault: (image: any) => void;
    onUploadFiles: (files: File[]) => void;
}

const TemplateSidebar: React.FC<TemplateSidebarProps> = ({
    show,
    onClose,
    role,
    loading,
    selectedThemeName,
    selectedNewTheme,
    isDefaultThemeByName,
    showNewCategoryInput,
    onShowNewCategoryInput,
    onCancelNewCategoryInput,
    newCategoryName,
    onChangeNewCategoryName,
    onSubmitNewCategory,
    categories,
    createList,
    uploadedImagesInfo,
    uploadedImages,
    onRestoreDefault,
    onUploadFiles,
}) => {
    if (!show) return null;

    const currentThemeKey = selectedThemeName || selectedNewTheme || '';
    const panelTranslateClass = show ? 'translate-x-0' : 'translate-x-full';

    return (
        <>
            <div
                className="absolute inset-0 z-10 rounded-[12px] bg-black/30"
                onClick={onClose}
            />

            <div
                className={`absolute inset-y-0 right-0 z-[11] flex w-[60%] max-w-[500px] transform flex-col overflow-hidden rounded-tr-[12px] rounded-br-[12px] bg-white shadow-[-10px_0_30px_rgba(0,0,0,0.2)] transition-transform duration-300 ease-in-out ${panelTranslateClass}`}
            >
                <div className="flex items-center justify-between border-b border-gray-200 bg-slate-50 px-6 py-5">
                    <h3 className="text-lg font-semibold text-slate-700">
                        {selectedNewTheme ? `${selectedNewTheme} (신규)` : selectedThemeName} - 카테고리
                    </h3>
                    <div className="flex items-center gap-2">
                        {role === 'ROLE_ADMIN' && !isDefaultThemeByName(selectedNewTheme ? selectedNewTheme : selectedThemeName) && (
                            <button
                                type="button"
                                onClick={onShowNewCategoryInput}
                                className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-600"
                            >
                                + 카테고리
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-200"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto px-6 py-5">
                    {loading ? (
                        <div className="template-loading">
                            <div className="template-spinner" />
                            <p>이미지를 불러오는 중...</p>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {showNewCategoryInput && (
                                <div className="rounded-lg border border-emerald-500 bg-emerald-50 p-4">
                                    <h4 className="mb-3 text-sm font-semibold text-emerald-600">새 카테고리 추가</h4>
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                        <input
                                            type="text"
                                            placeholder="카테고리 이름 입력"
                                            value={newCategoryName}
                                            onChange={(event) => onChangeNewCategoryName(event.target.value)}
                                            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={onSubmitNewCategory}
                                                className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                                            >
                                                추가
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onCancelNewCategoryInput}
                                                className="rounded-md bg-gray-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-600"
                                            >
                                                취소
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {categories.map((category, index) => (
                                <div key={`${category.name}-${index}`} className="space-y-4">
                                    <h4 className="rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-base font-semibold text-slate-700">
                                        {category.name}
                                    </h4>

                                    <div className="grid gap-3 rounded-lg bg-white p-3 shadow-sm ring-1 ring-inset ring-gray-200/70 [grid-template-columns:repeat(auto-fill,minmax(120px,1fr))]">
                                        {category.images.map((image: any) => {
                                            const updatedImage = createList.find(item => (
                                                item.cssName === image.cssName &&
                                                item.categoryName === image.categoryName &&
                                                item.themeName === selectedThemeName &&
                                                item.imageId === image.id
                                            ));
                                            const isUpdated = Boolean(updatedImage);
                                            const uploadKey = `${currentThemeKey}_${image.categoryName}_${image.cssName}`;
                                            const uploadInfo = uploadedImagesInfo.get(uploadKey);
                                            const isNew = Boolean(selectedNewTheme) ||
                                                String(image.id || '').startsWith('temp_') ||
                                                (!!uploadInfo && !uploadInfo?.isDummy && !image.id) ||
                                                createList.some(item => (
                                                    item.themeName === currentThemeKey &&
                                                    item.categoryName === image.categoryName &&
                                                    item.cssName === image.cssName &&
                                                    !item.imageId
                                                ));
                                            const canRestore = (role === 'ROLE_USER' || role === 'ROLE_MARKETING') && image.originFullFilePath &&
                                                image.originFullFilePath !== image.file?.fileFullUrl;

                                            const previewSrc = uploadedImages.get(uploadKey) || image.file?.fileFullUrl;

                                            return (
                                                <div
                                                    key={image.id || `${image.cssName}-${image.categoryName}`}
                                                    className="relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ring-1 ring-inset ring-gray-200/80"
                                                >
                                                    <div className="relative">
                                                        <img
                                                            src={previewSrc}
                                                            alt={image.cssName}
                                                            loading="lazy"
                                                            className="h-24 w-full object-cover"
                                                        />
                                                        {isNew ? (
                                                            <div className="absolute right-1 top-1 rounded bg-blue-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                                                신규
                                                            </div>
                                                        ) : isUpdated && (
                                                            <div className="absolute right-1 top-1 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                                                수정됨
                                                            </div>
                                                        )}
                                                        {canRestore && (
                                                            <button
                                                                type="button"
                                                                onClick={() => onRestoreDefault(image)}
                                                                className="absolute bottom-1 right-1 rounded bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white shadow hover:bg-red-600"
                                                            >
                                                                복구
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="px-3 py-2 text-center text-[11px] font-medium text-gray-600">
                                                        {image.cssName}
                                                        {image.width && image.height && (
                                                            <div className="mt-1 text-[10px] font-semibold text-red-500">
                                                                {getAspectRatioType(image.width, image.height)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                        <h5 className="mb-3 text-sm font-semibold text-slate-700">
                                            {category.name} 이미지 업로드
                                        </h5>
                                        <input
                                            type="file"
                                            accept=".jpg, .jpeg, .png"
                                            multiple
                                            onChange={(event) => {
                                                const selectedFiles = Array.from(event.target.files || []);
                                                const filesWithCategory = selectedFiles.map(file => {
                                                    (file as any).categoryName = category.name;
                                                    return file;
                                                });
                                                onUploadFiles(filesWithCategory);
                                            }}
                                            className="w-full cursor-pointer rounded border-2 border-dashed border-gray-300 px-3 py-2 text-xs text-gray-600 transition-colors hover:border-gray-400 focus:border-blue-500 focus:outline-none"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default TemplateSidebar;
